import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import WavyPatternBlack from 'assets/wavy_pattern_black.png';
import { Caption01 } from 'components/styles';

const Indicator = styled.div<{ color: string }>`
  height: var(--font-size-base);
  width: var(--font-size-base);
  background-color: ${({ color }) => color};
`;
const SoftAllocationIndicator = styled(Indicator)`
  position: relative;
  overflow: hidden;
  &:after {
    content: '';
    position: absolute;
    background-image: url(${WavyPatternBlack});
    background-size: contain;
    height: 200%;
    width: 250%;
    opacity: 50%;
  }
`;
const Label = styled.span`
  color: var(--gray-100);
  opacity: 0.5;
  ${Caption01};
`;
const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;
const Legends = styled.div`
  display: flex;
  gap: var(--spacing-05);
  align-items: center;
`;
const Container = styled.div`
  padding: var(--spacing-02) var(--spacing-07);
  box-shadow: var(--shadow-24);
  display: flex;
  justify-content: end;
  z-index: var(--zindex-elevated);
`;
const VerticalDivider = styled.div`
  border-right: 1px solid var(--cool-gray-30);
  height: var(--font-size-base);
  margin: 0 var(--spacing-03);
`;

const OverAllocatedLegend = () => (
  <Legend>
    <Indicator color="var(--red-90)" />
    <Label>
      <FormattedMessage id="resource_management.legends.over_allocated" />
    </Label>
  </Legend>
);
const MostlyAllocatedLegend = () => (
  <Legend>
    <Indicator color="var(--blue-80)" />
    <Label>
      <FormattedMessage id="resource_management.legends.mostly_allocated" />
    </Label>
  </Legend>
);
const ModeratelyAllocatedLegend = () => (
  <Legend>
    <Indicator color="var(--blue-50)" />
    <Label>
      <FormattedMessage id="resource_management.legends.moderately_allocated" />
    </Label>
  </Legend>
);
const MildlyAllocatedLegend = () => (
  <Legend>
    <Indicator color="var(--blue-20)" />
    <Label>
      <FormattedMessage id="resource_management.legends.mildly_allocated" />
    </Label>
  </Legend>
);
const SparselyAllocatedLegend = () => (
  <Legend>
    <Indicator color="var(--green-10)" />
    <Label>
      <FormattedMessage id="resource_management.legends.sparsely_allocated" />
    </Label>
  </Legend>
);
const SoftlyAllocatedLegend = () => (
  <Legend>
    <SoftAllocationIndicator color="var(--white-100)" />
    <Label>
      <FormattedMessage id="resource_management.legends.softly_allocated" />
    </Label>
  </Legend>
);
const CompanySummaryLegend = () => (
  <Legend>
    <Indicator color="var(--yellow-40)" />
    <Label>
      <FormattedMessage id="resource_management.legends.company" />
    </Label>
  </Legend>
);

const PlaceholderSummaryLegend = () => (
  <Legend>
    <Indicator color="var(--yellow-20)" />
    <Label>
      <FormattedMessage id="resource_management.legends.placeholder" />
    </Label>
  </Legend>
);

export const LegendsForPeopleTab = (): JSX.Element => (
  <Legends>
    <OverAllocatedLegend />
    <MostlyAllocatedLegend />
    <ModeratelyAllocatedLegend />
    <MildlyAllocatedLegend />
    <SparselyAllocatedLegend />
    <VerticalDivider />
    <CompanySummaryLegend />
    <PlaceholderSummaryLegend />
    <SoftlyAllocatedLegend />
  </Legends>
);

export const ResourceManagementLegendsForPeopleTab = (): JSX.Element => (
  <Container>
    <LegendsForPeopleTab />
  </Container>
);
